// Layer 4 (UI) — the MTBF / MTTR tab (a20-maintenance S3, rebuilt to the
// locked comp `downtime.html` A). Renders the equipment-class MTBF read
// against its targets; every number on the glass comes from
// Reliability.transform.ts over `itemKpis` and the YTD event log, nothing is
// typed here. The class targets and the two policy bands are the only typed
// numbers behind it, and they live in the seed.
//
// The Actual MTBF column is the chart: one bar per class on a shared scale
// (`mtbfBarScale`), a tick at the class target, so the table carries the
// actual-vs-target read the comp draws in a single card.

import { ArrowDown, ArrowRight, ArrowUp, type LucideIcon } from 'lucide-react';
import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { KpiCard, vizToneColor, type VizTone } from '@/versions/v1/modules/_viz';
import { Commentable } from '@/shared/comments/Commentable';
import { formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { KpiStrip } from '../maintenance/KpiStrip';
import { downtimeEvents } from '../maintenance/DowntimeEvents.transform';
import { formatHours } from '../maintenance/Downtime.transform';
import { CARD_TABLE, DowntimeCard, HOT_ROW, RankBar, SYN_CHIP } from './DowntimeCard';
import {
  mtbfBarScale,
  reliabilityBarRows,
  reliabilityKpis,
  reliabilityRows,
  type ReliabilityTrend,
} from './Reliability.transform';

/** `ok` when the count is complete, `warn` when partial, `bad` when zero — the
 *  same three-step read for both count cards. */
function countTone(count: number, total: number): VizTone {
  if (total > 0 && count === total) return 'ok';
  return count > 0 ? 'warn' : 'bad';
}

const TREND_CELL: Record<ReliabilityTrend, { Icon: LucideIcon; label: string; tone: VizTone }> = {
  improving: { Icon: ArrowUp, label: 'improving', tone: 'ok' },
  worsening: { Icon: ArrowDown, label: 'worsening', tone: 'bad' },
  stable: { Icon: ArrowRight, label: 'stable', tone: 'neutral' },
};

export function ReliabilityTab() {
  const rows = reliabilityRows(downtimeEvents());
  const kpis = reliabilityKpis(rows);
  const bars = reliabilityBarRows(rows);
  const scale = mtbfBarScale(bars);

  return (
    <Commentable name="Reliability">
      <div className="space-y-3">
        <KpiStrip>
          <KpiCard
            label="At MTBF target"
            value={`${kpis.atTargetCount} of ${kpis.classCount}`}
            sub="classes, 30-day basis"
            tone={countTone(kpis.atTargetCount, kpis.classCount)}
            className="px-3.5 py-3"
          />
          <KpiCard
            label="Worst vs target"
            value={`${Math.round(kpis.worst.pctOfTarget)}%`}
            sub={`${kpis.worst.label} · ${formatHours(kpis.worst.actualMtbfHours)} vs ${kpis.worst.targetMtbfHours} h`}
            tone={kpis.worst.band}
            className="px-3.5 py-3"
          />
          <KpiCard
            label="Best vs target"
            value={`${Math.round(kpis.best.pctOfTarget)}%`}
            sub={`${kpis.best.label} · ${formatHours(kpis.best.actualMtbfHours)} vs ${kpis.best.targetMtbfHours} h`}
            tone={kpis.best.band}
            className="px-3.5 py-3"
          />
          <KpiCard
            label="MTTR within target"
            value={`${kpis.mttrWithinTargetCount} of ${kpis.mttrMeasuredCount}`}
            sub="classes with a repair this basis"
            tone={countTone(kpis.mttrWithinTargetCount, kpis.mttrMeasuredCount)}
            className="px-3.5 py-3"
          />
        </KpiStrip>

        <DowntimeCard
          title="MTBF against target"
          meta={`${kpis.basisLabel}, 30-day basis · bar = actual · tick = target`}
          actions={SYN_CHIP}
        >
          <div className={CARD_TABLE}>
            <Table wrap={false} className="min-w-[860px] tabular-nums">
              <THead>
                <TR>
                  <TH>Class</TH>
                  <TH className="text-right">Events (30d)</TH>
                  <TH className="w-[22%]">Actual MTBF</TH>
                  <TH className="text-right">Target</TH>
                  <TH className="text-right">Gap</TH>
                  <TH className="text-right">% of target</TH>
                  <TH className="text-right">MTTR</TH>
                  <TH className="text-right">MTTR target</TH>
                  <TH className="text-right">RPN</TH>
                  <TH>Trend</TH>
                  <TH>Expected next</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((row) => {
                  const trend = TREND_CELL[row.trend];
                  return (
                    <TR key={row.classId} className={row.classId === kpis.worst.classId ? HOT_ROW : undefined}>
                      <TD>
                        <span className="block whitespace-nowrap font-medium text-ink-primary">{row.label}</span>
                        <span className="block whitespace-nowrap text-[10px] tracking-[0.04em] text-muted">
                          {row.itemIds.join(' · ')}
                        </span>
                      </TD>
                      <TD className="whitespace-nowrap text-right">{row.eventCount}</TD>
                      <TD>
                        <span className="flex items-center gap-2">
                          <span className="relative min-w-0 flex-1">
                            <RankBar ratio={row.actualMtbfHours / scale} tone={row.band} />
                            <span
                              aria-label={`${row.targetMtbfHours} h target`}
                              className="absolute -inset-y-[3px] w-0.5 rounded-[1px] bg-ink-primary"
                              style={{ left: `calc(${(row.targetMtbfHours / scale) * 100}% - 1px)` }}
                            />
                          </span>
                          <span className="w-14 shrink-0 whitespace-nowrap text-right font-semibold text-ink-primary">
                            {formatHours(row.actualMtbfHours)}
                          </span>
                        </span>
                      </TD>
                      <TD className="whitespace-nowrap text-right">{row.targetMtbfHours} h</TD>
                      <TD className="whitespace-nowrap text-right">
                        {(row.gapHours < 0 ? '−' : '') + formatHours(Math.abs(row.gapHours))}
                      </TD>
                      <TD className="whitespace-nowrap text-right font-semibold" style={{ color: vizToneColor(row.band) }}>
                        {Math.round(row.pctOfTarget)}%
                      </TD>
                      <TD className="whitespace-nowrap text-right">
                        {row.actualMttrHours === null ? '—' : formatHours(row.actualMttrHours)}
                      </TD>
                      <TD className="whitespace-nowrap text-right">{row.targetMttrHours} h</TD>
                      <TD className="whitespace-nowrap text-right">{row.rpn ?? '—'}</TD>
                      <TD className="whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: vizToneColor(trend.tone) }}>
                          <trend.Icon aria-hidden="true" className="h-3 w-3 shrink-0 translate-y-px" strokeWidth={2.25} />
                          {trend.label}
                        </span>
                      </TD>
                      <TD className="whitespace-nowrap">
                        {row.expectedNext === null ? '—' : formatDayMonth(row.expectedNext)}
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </div>
        </DowntimeCard>
      </div>
    </Commentable>
  );
}
